import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { recordLinkFollow } from '@/lib/usageEvents'
import { Link } from 'react-router-dom'
import { useGroupsFeedShowVendorGroupsFlag } from '@/hooks/useGroupsFeedShowVendorGroupsFlag'

interface ActivitySidebarProps {
  userId: string
}

interface GroupMember {
  group_id: string
  groups: {
    id: string
    name: string
    group_type?: string | null
  }
}

interface LinkFavorite {
  link_id: string
  links: {
    id: string
    name: string
    url: string
  }
}

export default function ActivitySidebar({ userId }: ActivitySidebarProps) {
  const { showVendorGroupsInFeed } = useGroupsFeedShowVendorGroupsFlag()

  // Fetch user's groups (optionally exclude vendor groups per feature flag)
  const { data: groups } = useQuery({
    queryKey: ['user-groups-sidebar', userId, showVendorGroupsInFeed],
    queryFn: async () => {
      try {
        const { data, error } = await supabase
          .from('group_members')
          .select(`
            group_id,
            groups (
              id,
              name,
              group_type
            )
          `)
          .eq('user_id', userId)
          .limit(10)
          .order('joined_at', { ascending: false })

        if (error) {
          console.warn('Error fetching groups:', error)
          return []
        }

        const transformed = (data || []).map((member: any) => ({
          group_id: member.group_id,
          groups: Array.isArray(member.groups) ? member.groups[0] : member.groups,
        })) as GroupMember[]

        const isVendor = (g: { group_type?: string | null }) =>
          g.group_type === 'broadcaster' || g.group_type === 'ad-creator'
        const filtered = showVendorGroupsInFeed
          ? transformed
          : transformed.filter((member) => {
              const group = member.groups
              return !group?.group_type || !isVendor(group)
            })

        return filtered.slice(0, 2)
      } catch (err) {
        console.warn('Error fetching groups:', err)
        return []
      }
    },
    retry: false,
  })

  // Fetch user's links (created by user, not favorites)
  const { data: links } = useQuery({
    queryKey: ['user-links-sidebar', userId],
    queryFn: async () => {
      try {
        const { data, error } = await supabase
          .from('links')
          .select(`
            id,
            name,
            url
          `)
          .eq('created_by', userId)
          .limit(2)
          .order('created_at', { ascending: false })

        if (error) {
          // If table doesn't exist or RLS issue, return empty array
          console.warn('Error fetching links:', error)
          return []
        }
        // Transform to match the expected structure
        return (data || []).map((link) => ({
          link_id: link.id,
          links: {
            id: link.id,
            name: link.name,
            url: link.url,
          },
        })) as LinkFavorite[]
      } catch (err) {
        // Gracefully handle any errors
        console.warn('Error fetching links:', err)
        return []
      }
    },
    retry: false, // Don't retry on 400 errors
  })

  return (
    <div className="space-y-6">
      {/* My Groups Section */}
      <div 
        className="rounded-lg p-6 border-2"
        style={{
          backgroundColor: 'var(--bg-secondary)',
          borderColor: 'var(--bg-tertiary)',
        }}
      >
        <h2 
          className="text-xl font-bold mb-2"
          style={{ color: 'var(--text-primary)' }}
        >
          My Groups
        </h2>
        <p 
          className="text-sm mb-4"
          style={{ color: 'var(--text-primary)' }}
        >
          Your subscribed communities
        </p>
        
        {groups && groups.length > 0 ? (
          <div className="space-y-2 mb-4">
            {groups.map((member) => {
              const group = member.groups
              if (!group) return null
              
              return (
                <Link
                  key={member.group_id}
                  to={`/groups/${group.id}`}
                  className="block p-3 rounded border-2 cursor-pointer transition-opacity hover:opacity-80"
                  style={{
                    backgroundColor: 'var(--bg-tertiary)',
                    borderColor: 'var(--bg-tertiary)',
                    textDecoration: 'none',
                  }}
                >
                  <p 
                    className="font-semibold"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    {group.name}
                  </p>
                </Link>
              )
            })}
          </div>
        ) : (
          <p 
            className="text-sm mb-4"
            style={{ color: 'var(--text-primary)' }}
          >
            No groups yet
          </p>
        )}
        
        <Link
          to="/groups"
          className="block w-full text-center px-4 py-2 font-semibold rounded"
          style={{
            backgroundColor: 'var(--bg-interactive)',
            color: 'var(--text-inverse)',
          }}
        >
          View All Groups
        </Link>
      </div>

      {/* My Links Section */}
      <div 
        className="rounded-lg p-6 border-2"
        style={{
          backgroundColor: 'var(--bg-secondary)',
          borderColor: 'var(--bg-tertiary)',
        }}
      >
        <h2 
          className="text-xl font-bold mb-2"
          style={{ color: 'var(--text-primary)' }}
        >
          My Links
        </h2>
        <p 
          className="text-sm mb-4"
          style={{ color: 'var(--text-primary)' }}
        >
          Your saved website links
        </p>
        
        {links && links.length > 0 ? (
          <div className="space-y-2 mb-4">
            {links.map((favorite) => {
              const link = favorite.links
              if (!link) return null
              
              return (
                <a
                  key={favorite.link_id}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block p-3 rounded border-2 cursor-pointer transition-opacity hover:opacity-80"
                  style={{
                    backgroundColor: 'var(--bg-tertiary)',
                    borderColor: 'var(--bg-tertiary)',
                    textDecoration: 'none',
                  }}
                  onClick={async (e) => {
                    e.preventDefault()
                    await recordLinkFollow(supabase, link.id, userId)
                    window.open(link.url, '_blank', 'noopener,noreferrer')
                  }}
                >
                  <p 
                    className="font-semibold mb-1"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    {link.name}
                  </p>
                  <p 
                    className="text-xs truncate"
                    style={{ color: 'var(--text-secondary)' }}
                  >
                    {link.url}
                  </p>
                </a>
              )
            })}
          </div>
        ) : (
          <p 
            className="text-sm mb-4"
            style={{ color: 'var(--text-primary)' }}
          >
            No links yet
          </p>
        )}
        
        <Link
          to="/links"
          className="block w-full text-center px-4 py-2 font-semibold rounded"
          style={{
            backgroundColor: 'var(--bg-interactive)',
            color: 'var(--text-inverse)',
          }}
        >
          View All Links
        </Link>
      </div>
    </div>
  )
}

